/**
 * Planer i RCP — wnioski pracownika w panelu i ustawienia „kto akceptuje” / rodzaje wniosków (`pln.requests.*`).
 * Odpowiedź LiluFresh 28.10: handlowiec składa wniosek urlopowy w panelu i w apce, akceptuje zarząd, L4 nigdzie.
 */
import type { Shape } from './planner-worktime-pl';

export const plnRequestsPl = {
  approverWho: { MANAGER: 'przełożonego', BOARD: 'zarządu', MANAGER_OR_BOARD: 'przełożonego lub zarządu' },
  my: {
    title: 'Moje wnioski',
    description: 'Urlop, delegacja, szkolenie i inne — wniosek trafia do akceptacji {who}',
    vacation: 'Wniosek urlopowy',
    other: 'Inny wniosek',
    empty: 'Nie masz jeszcze żadnych wniosków',
    emptyBody: 'Złóż wniosek urlopowy — dostaniesz powiadomienie, gdy zostanie rozpatrzony.',
    pending: 'Oczekujące: {n}',
    cancel: 'Anuluj wniosek',
    cancelTitle: 'Anulować wniosek?',
    cancelApproved: 'Wniosek jest zatwierdzony — po anulowaniu dni nieobecności zejdą z grafiku.',
    cancelled: 'Wniosek anulowany',
    respondedBy: 'Rozpatrzył(a): {name}',
    response: 'Odpowiedź',
    days: '{n} dni',
    day: '1 dzień',
  },
  form: {
    title: 'Nowy wniosek',
    type: 'Rodzaj wniosku',
    from: 'Od',
    to: 'Do (opcjonalnie)',
    preferredShift: 'Preferowana zmiana',
    anyShift: 'Dowolna',
    notes: 'Uwagi',
    notesPh: 'Np. powód, zastępstwo, kontakt w czasie urlopu',
    submit: 'Wyślij wniosek',
    sent: 'Wniosek wysłany do akceptacji',
    failed: 'Nie udało się wysłać wniosku',
    endBeforeStart: 'Data „do” nie może być wcześniejsza niż „od”',
    startRequired: 'Podaj datę początku',
    goesTo: 'Wniosek trafi do akceptacji {who}.',
  },
  drawer: {
    notYours: 'Wnioski w tej organizacji akceptuje {who}',
  },
  settings: {
    title: 'Wnioski i nieobecności',
    description: 'Kto akceptuje wnioski pracowników i które rodzaje wniosków i nieobecności organizacja prowadzi.',
    approver: 'Kto akceptuje wnioski',
    approverOpt: { MANAGER: 'Przełożony', BOARD: 'Zarząd', MANAGER_OR_BOARD: 'Przełożony lub zarząd' },
    approverHint: {
      MANAGER: 'Przełożony pracownika (kierownik z prawem układania grafiku); bez przełożonego — osoby układające grafik całej firmy.',
      BOARD: 'Członkowie zarządu. Nikt nie zatwierdza własnego wniosku; gdy w zarządzie nie ma nikogo innego, decyduje przełożony.',
      MANAGER_OR_BOARD: 'Przełożony albo dowolny członek zarządu — decyduje ten, kto pierwszy rozpatrzy wniosek.',
    },
    requestTypes: 'Rodzaje wniosków',
    absenceCodes: 'Rodzaje nieobecności',
    on: 'Włączony',
    off: 'Wyłączony',
    disabledHint: 'Wyłączonego rodzaju nie da się złożyć ani wpisać; dane zapisane wcześniej nie są pokazywane w grafiku, wnioskach, RCP ani zestawieniach. Wyłączenie kodu nieobecności wyłącza też wniosek, który go zapisuje.',
    save: 'Zapisz',
    saved: 'Zapisano ustawienia wniosków',
    dirty: 'Masz niezapisane zmiany',
  },
};

export const plnRequestsEn: Shape<typeof plnRequestsPl> = {
  approverWho: { MANAGER: 'your manager', BOARD: 'the board', MANAGER_OR_BOARD: 'your manager or the board' },
  my: {
    title: 'My requests',
    description: 'Leave, business trip, training and more — the request goes to {who} for approval',
    vacation: 'Leave request',
    other: 'Other request',
    empty: 'You have no requests yet',
    emptyBody: 'Submit a leave request — you will be notified when it is decided.',
    pending: 'Pending: {n}',
    cancel: 'Cancel request',
    cancelTitle: 'Cancel the request?',
    cancelApproved: 'The request is approved — cancelling removes its absence days from the schedule.',
    cancelled: 'Request cancelled',
    respondedBy: 'Decided by: {name}',
    response: 'Response',
    days: '{n} days',
    day: '1 day',
  },
  form: {
    title: 'New request',
    type: 'Request type',
    from: 'From',
    to: 'To (optional)',
    preferredShift: 'Preferred shift',
    anyShift: 'Any',
    notes: 'Notes',
    notesPh: 'E.g. reason, cover, contact during leave',
    submit: 'Send request',
    sent: 'Request sent for approval',
    failed: 'Could not send the request',
    endBeforeStart: 'The end date cannot be before the start date',
    startRequired: 'Enter the start date',
    goesTo: 'The request goes to {who} for approval.',
  },
  drawer: {
    notYours: 'In this organization requests are approved by {who}',
  },
  settings: {
    title: 'Requests and absences',
    description: 'Who approves employee requests and which request and absence types the organization keeps.',
    approver: 'Who approves requests',
    approverOpt: { MANAGER: 'Manager', BOARD: 'Board', MANAGER_OR_BOARD: 'Manager or board' },
    approverHint: {
      MANAGER: "The employee's manager (with schedule rights); without a manager — people who plan the whole company.",
      BOARD: 'Board members. Nobody approves their own request; if there is no other board member, the manager decides.',
      MANAGER_OR_BOARD: 'The manager or any board member — whoever decides first.',
    },
    requestTypes: 'Request types',
    absenceCodes: 'Absence types',
    on: 'On',
    off: 'Off',
    disabledHint: 'A disabled type cannot be submitted or entered; data saved earlier is not shown in the schedule, requests, time records or reports. Disabling an absence code also disables the request that records it.',
    save: 'Save',
    saved: 'Request settings saved',
    dirty: 'You have unsaved changes',
  },
};
